import React from "react";
import ServiceHeroSection from "../common/service-hero-section";
import { AiBlog } from "@/components/main";
import {
  FeatureGrid,
  OutcomesList,
  ProcessSteps,
  RelatedServices,
  ScenarioGrid,
  ServiceAudience,
  ServiceBottomCta,
  ServiceFaq,
  ServiceSection,
  ServiceSectionHeader,
  SplitContent,
} from "../common/service-content-blocks";

const CAPABILITIES = [
  {
    title: "Document intake automation",
    description:
      "Classify, extract, validate, and route documents into downstream systems with exception queues.",
  },
  {
    title: "Approval and handoff flows",
    description:
      "Move work between people and systems with rules, models, and clear ownership at each step.",
  },
  {
    title: "Status and exception handling",
    description:
      "Detect delays, missing data, and SLA risk, then notify the right owner with context.",
  },
  {
    title: "Human review checkpoints",
    description:
      "Keep judgment where it belongs without forcing every item through a manual path.",
  },
  {
    title: "System writes with audit",
    description:
      "Update records through APIs with logs operators can inspect when something looks wrong.",
  },
  {
    title: "Measurement and tuning",
    description:
      "Track throughput, error rates, and rework so automation improves after launch.",
  },
];

const HOW_IT_WORKS = [
  {
    title: "Capture",
    description:
      "Ingest emails, uploads, forms, or system events that start the workflow.",
  },
  {
    title: "Understand",
    description:
      "Classify and extract with models and rules tuned to your document and data patterns.",
  },
  {
    title: "Decide and act",
    description:
      "Apply policy, route work, update systems, or request human review.",
  },
  {
    title: "Observe",
    description:
      "Monitor outcomes, surface failures, and feed improvements back into the flow.",
  },
];

const SCENARIOS = [
  {
    title: "Invoice and claims processing",
    description:
      "Extract fields, validate against rules, and push clean records into finance or claims systems.",
    outcome: "Less keying, faster cycle time, clearer exceptions.",
  },
  {
    title: "Onboarding and compliance packets",
    description:
      "Collect required documents, check completeness, and escalate missing items automatically.",
    outcome: "Fewer stalled packets and fewer status chases.",
  },
  {
    title: "Ops request routing",
    description:
      "Classify inbound requests and assign them with context to the right queue.",
    outcome: "Lower misroutes and faster first response.",
  },
  {
    title: "Shipment document packs",
    description:
      "Assemble and validate shipping paperwork before handoff to partners or customs.",
    outcome: "Fewer rejected packs and less last-minute firefighting.",
  },
];

const DELIVERY = [
  {
    title: "Map the process",
    description: "Document steps, owners, systems, and failure modes.",
  },
  {
    title: "Design the automation",
    description: "Define rules, model roles, and human review points.",
  },
  {
    title: "Pilot on real volume",
    description: "Run against live samples and measure rework.",
  },
  {
    title: "Scale with ownership",
    description: "Harden monitoring and hand off runbooks to operators.",
  },
];

export const FAQS = [
  {
    question: "Is this RPA or AI?",
    answer:
      "Usually both. Rules and integrations handle deterministic steps. Models help with documents, classification, and messy inputs. We pick the simplest approach that works.",
  },
  {
    question: "What if accuracy is not perfect?",
    answer:
      "We design exception queues and confidence thresholds so uncertain items go to people. Automation should reduce load, not hide errors.",
  },
  {
    question: "Can we start with one process?",
    answer:
      "Yes. Most clients start with a single high-volume flow, prove value, then expand.",
  },
];

function AiWorkflowAutomationComponent({ posts = [] }) {
  return (
    <>
      <ServiceHeroSection
        headlinePrimary="Automate the work"
        headlineHighWhite="between people and systems"
        descriptionPrimary="We design automation for document flows, approvals, and operational handoffs using models, rules, and human review."
        descriptionSecondary="Built to raise throughput without removing accountability."
      />

      <ServiceAudience
        forItems={[
          "You have a high-volume, repeatable process with clear owners",
          "Documents or messy inputs create manual bottlenecks",
          "Systems can receive structured updates via API or integration",
          "You can define exception handling up front",
        ]}
        notForItems={[
          "Every case requires novel judgment with no patterns",
          "There is no process owner or success metric",
          "Systems cannot be written to safely",
          "You want full autonomy with no review path",
        ]}
      />

      <ServiceSection>
        <ServiceSectionHeader
          title="What we automate"
          lead="Practical automation patterns for operational teams."
        />
        <FeatureGrid items={CAPABILITIES} />
      </ServiceSection>

      <ServiceSection muted>
        <SplitContent
          title="How automation works"
          lead="A simple loop from capture to action to observation."
          points={HOW_IT_WORKS}
        />
      </ServiceSection>

      <ServiceSection>
        <ServiceSectionHeader
          title="Common starting points"
          lead="Processes where automation usually pays for itself quickly."
        />
        <ScenarioGrid items={SCENARIOS} />
      </ServiceSection>

      <ServiceSection muted>
        <ServiceSectionHeader
          title="How we deliver"
        />
        <ProcessSteps steps={DELIVERY} />
      </ServiceSection>

      <OutcomesList
        title="Outcomes we target"
        lead="We define metrics before the pilot starts."
        outcomes={[
          "Higher throughput on the target process",
          "Lower manual touches per item",
          "Exception queues operators trust",
          "Auditability of automated actions",
        ]}
      />

      <ServiceFaq items={FAQS} />
      <ServiceBottomCta />
      <RelatedServices
        items={[
          { href: "/ai-agent-development", label: "AI agents" },
          { href: "/custom-llm-application-development", label: "LLM applications" },
          { href: "/ai-integrations", label: "Integrations" },
          { href: "/mvp-poc-development", label: "MVP and PoC" },
        ]}
      />
      <AiBlog posts={posts} />
    </>
  );
}

export default AiWorkflowAutomationComponent;
